'use client';

import { ReactElement, useMemo } from 'react';
import { Controller, SubmitHandler, useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { CountryIso2 } from 'react-international-phone';
import { zodResolver } from '@hookform/resolvers/zod';
import * as Sentry from '@sentry/nextjs';
import { toast } from 'sonner';
import { z } from 'zod';

import { getAuthMnemonicFromError } from '@nutralab/api';
import { Button, FormGroup, PasswordInput, PhoneInput, Text } from '@nutralab/ui';

import { classNames } from '@m1work/shared-utils';

import styles from './LoginModal.module.scss';

import { AuthModalLayout } from '@/app/_modals/_components/AuthModalLayout';
import { RegisterModal, type RegisterModalData } from '@/app/_modals/RegisterModal';
import { ResetPasswordModal, type ResetPasswordModalData } from '@/app/_modals/ResetPasswordModal';
import { useAuth } from '@/app/_providers/AuthProvider';
import { useModal } from '@/app/_providers/ModalProvider';
import { useSiteConfig } from '@/app/_providers/SiteConfigProvider';
import { storefrontAuth } from '@/shared/api/browser';
import { PASSWORD_MIN_LENGTH } from '@/shared/constants';
import { ModalProps } from '@/shared/types';
import { phoneField, stringRequired, translateAuthMessage } from '@/shared/utils';

const createSchema = (
    t: ReturnType<typeof useTranslation>['t'],
): z.ZodObject<{
    phone: z.ZodEffects<z.ZodString, string, string>;
    password: z.ZodString;
}> =>
    z.object({
        phone: phoneField(t),
        password: stringRequired(t).min(
            PASSWORD_MIN_LENGTH,
            t('validation.password.min-length', { length: PASSWORD_MIN_LENGTH }),
        ),
    });

type FormValues = z.infer<ReturnType<typeof createSchema>>;

export interface LoginModalData {
    referralCode?: string;
}

type LoginModalProps = ModalProps<LoginModalData>;

/** Sign-in with phone and password, with links to registration and password reset. */
export function LoginModal({ onClose, zIndex, data }: LoginModalProps): ReactElement {
    const { t, i18n } = useTranslation();
    const { country } = useSiteConfig();
    const { setSession } = useAuth();
    const { openModal } = useModal();
    const schema = useMemo(() => createSchema(t), [t]);
    const {
        control,
        register,
        handleSubmit,
        resetField,
        formState: { errors, isSubmitting },
    } = useForm<FormValues>({
        resolver: zodResolver(schema),
        mode: 'onTouched',
    });
    const phoneValue = useWatch({ control, name: 'phone' });

    const onSubmit: SubmitHandler<FormValues> = async (formData) => {
        try {
            const tokens = await storefrontAuth.login({ phone: formData.phone, password: formData.password });
            await setSession(tokens);
            onClose();
        } catch (err) {
            const mnemonic = getAuthMnemonicFromError(err);
            if (!mnemonic || mnemonic.message === 'auth.internal_error') {
                Sentry.captureException(err);
            }
            toast.error(translateAuthMessage(t, mnemonic?.message, 'auth.login.error'));
            resetField('password');
        }
    };

    const handleOpenRegister = (): void => {
        openModal<RegisterModalData>({ ModalComponent: RegisterModal, data: { referralCode: data?.referralCode } });
        onClose();
    };

    const handleOpenResetPassword = (): void => {
        openModal<ResetPasswordModalData>({ ModalComponent: ResetPasswordModal, data: { phone: phoneValue } });
        onClose();
    };

    return (
        <AuthModalLayout onClose={onClose} zIndex={zIndex} title={t('auth.login.title')}>
            <Text align="center" size="md" className="mb-2">
                {t('auth.login.no-account')}{' '}
                <button type="button" className={styles.link} onClick={handleOpenRegister}>
                    {t('auth.login.create-account')}
                </button>
            </Text>

            <form className={classNames(styles.form, 'mt-4')} onSubmit={(event) => void handleSubmit(onSubmit)(event)}>
                <FormGroup required label={t('auth.field.phone.label')} errorText={errors.phone?.message}>
                    <Controller
                        name="phone"
                        control={control}
                        render={({ field }) => (
                            <PhoneInput
                                value={field.value}
                                onChange={field.onChange}
                                defaultCountry={country.code.toLowerCase() as CountryIso2}
                                locale={i18n.language}
                                countryButtonLabel={t('form.phone.country-button')}
                                countryListLabel={t('form.phone.country-list')}
                                error={Boolean(errors.phone)}
                                placeholder={t('auth.field.phone.placeholder')}
                            />
                        )}
                    />
                </FormGroup>

                <FormGroup required label={t('auth.field.password.label')} errorText={errors.password?.message}>
                    <PasswordInput
                        {...register('password')}
                        showPasswordLabel={t('form.password.show')}
                        hidePasswordLabel={t('form.password.hide')}
                        placeholder={t('auth.field.password.placeholder')}
                        error={Boolean(errors.password)}
                    />
                </FormGroup>

                <button type="button" className={styles.link} onClick={handleOpenResetPassword}>
                    {t('auth.reset-password.link')}
                </button>

                <Button className="mt-4" type="submit" size="lg" disabled={isSubmitting}>
                    {t('auth.login.submit')}
                </Button>
            </form>
        </AuthModalLayout>
    );
}
